import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TimezoneService } from '@core/services/timezone.service';
import {
  Country,
  NOTIFICATION_TYPE_OPTIONS,
  NotificationEditConfig,
  NotificationListItem,
  NotificationSaveConfig,
  NotificationType,
  US_COUNTRY_CODE
} from '@shared/models';
import * as SimpleCKEditor from 'lib/ckeditor/build/ckeditor';
import { merge, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ComponentContext } from '../../../modules/modal/modal.model';
import { ModalService } from '../../../modules/modal/modal.service';

export interface AlertAndNotificationModalData {
  isEditMode?: boolean;
  notification: NotificationListItem;
  countries: Country[];
}

@Component({
  selector: 'gcc-notification-modal',
  templateUrl: './notification-modal.component.html',
  styleUrls: ['./notification-modal.component.scss']
})
export class NotificationModalComponent
  implements OnInit, ComponentContext<AlertAndNotificationModalData, NotificationSaveConfig> {
  @Input() public isEditMode = false;
  @Input() public notification: NotificationListItem;
  @Input() public countries: Country[];

  @Input() public onClose: () => void;
  @Input() public onAction: (value: NotificationSaveConfig) => void;

  public readonly TITLE_MAX_LENGTH = 100;
  public readonly BRIEF_DESCRIPTION_MAX_LENGTH = 200;
  public readonly DESCRIPTION_MAX_LENGTH = 2000;

  public form: FormGroup;

  public ckEditor = SimpleCKEditor;
  public timezone$: Observable<string>;

  public typeOptions = NOTIFICATION_TYPE_OPTIONS;
  public notificationType = NotificationType;
  public isCopy: boolean;
  public countriesSelectorData: Array<{
    label: string;
    value: string;
  }>;
  get currentDate() {
    return this.timezoneService.shiftToLocal(new Date());
  }

  constructor(private fb: FormBuilder, private timezoneService: TimezoneService, private modalService: ModalService) {}

  public ngOnInit(): void {
    this.isCopy = Boolean(this.notification && !this.notification.notificationId);

    this.initForm();
    if (this.notification) {
      this.timezone$ = merge(of(this.notification.endDate), this.form.get('endDate').valueChanges).pipe(
        map(date => this.timezoneService.getTimezoneOffset(date))
      );
    } else {
      this.timezone$ = this.form.get('endDate').valueChanges.pipe(map(d => this.timezoneService.getTimezoneOffset(d)));
    }
    this.countriesSelectorData = this.countries
      .map((item: Country) => {
        return {
          label: item.name,
          value: item.countryCode
        };
      })
      .filter(item => (this.isEditMode ? item.value === this.form.value.countryCode : true));
    // User Story 1806543: Alerts -- Edit Member Firms Field for Alerts
  }

  public onSave(isPublish?: boolean) {
    if (this.form.invalid) {
      return;
    }

    const { countryCode, endDate, type, title, briefDescription, description } = this.form
      .value as NotificationListItem;
    const convertedEndDate = this.timezoneService.shiftFromLocal(endDate);
    const notificationConfig: NotificationSaveConfig | NotificationEditConfig = {
      notification: {
        countryCodes: countryCode,
        endDate: convertedEndDate,
        type,
        title,
        briefDescription,
        description
      },
      isPublish: Boolean(isPublish)
    };

    if (this.isEditMode) {
      (notificationConfig as NotificationEditConfig).notification = {
        notificationId: this.notification.notificationId,
        ...notificationConfig.notification
      };
    }

    if (!isPublish) {
      this.onAction(notificationConfig);
    } else {
      this.modalService
        .confirm$({
          title: 'notifications.titles.publish',
          message: [
            'notifications.messages.publish_confirmation.question',
            'notifications.messages.publish_confirmation.description'
          ]
        })
        .subscribe(() => {
          this.onAction(notificationConfig);
        });
    }
  }

  private initForm() {
    const { endDate = null, type = NotificationType.Alert, title = null, briefDescription = null, description = null } =
      this.notification || {};
    let convertedEndDate = endDate;

    const countryCode = this.isEditMode
      ? this.notification?.countryCode // EDIT
      : [this.notification?.countryCode || US_COUNTRY_CODE]; // COPY || ADD;

    if (endDate) {
      convertedEndDate = this.timezoneService.shiftToLocal(endDate);
    }

    this.form = this.fb.group({
      countryCode: [countryCode, Validators.required],
      endDate: [convertedEndDate, Validators.required],
      type: [type, Validators.required],
      title: [title, { validators: [Validators.required, Validators.maxLength(this.TITLE_MAX_LENGTH)] }],
      briefDescription: [briefDescription],
      description: [description]
    });
  }
}
